import React, {useEffect, useRef} from 'react';
import {
    ActionIcon,
    Autocomplete,
    Center,
    Divider,
    Flex,
    NavLink,
    NumberInput,
    TextInput,
    Tooltip,
} from '@mantine/core';
import {IconEdit, IconInvoice, IconSearch, IconUser} from '@tabler/icons-react';
import {useForm} from '@mantine/form';
import {useTranslation} from 'react-i18next';
import {Link, useNavigate} from 'react-router-dom';
import {MemberModelAPI} from '@bobex/react-core/domain/MemberModel/MemberModelAPI';
import {MemberAutocompleteView} from '@bobex/react-core/domain/MemberModel/view/MemberAutocompleteView';
import {Page} from '@bobex/react-core/data/Page';
import {AccountService} from '@bobex/react-core/services/AccountService';
import {
    AdministratorModelConstants
} from '@bobex/react-core/domain/AdministratorModel/constants/AdministratorModelConstants';
import {MemberSearchRequestDTO} from '@bobex/react-core/domain/MemberModel/dto/MemberSearchRequestDTO';
import '@bobex/react-core/components/GenericSearch/searchForm.css';
import {UserGroupColorUtils} from '@bobex/react-core/utils/UserGroupColorUtils';
import {UserGroupEnum} from '@bobex/react-core/domain/UserGroupModel/constants/UserGroupEnum';
import {MemberStatusIcon} from '@bobex/react-core/components/MemberStatusIcon';
import {PartnerRfpUtils} from '@bobex/react-core/domain/PartnerRfpModel/PartnerRfpUtils';

export type SearchDrawerTab = 'search' | 'incall';

interface SearchHeaderTabContentProps {
  onClose: () => void;
}

export default function SearchHeaderTabContent({ onClose }: SearchHeaderTabContentProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [companyList, setCompanyList] = React.useState<Page<MemberAutocompleteView>>(
    new Page<MemberAutocompleteView>(MemberAutocompleteView)
  );
  const companyInputRef = useRef<HTMLInputElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);

  const leadIdForm = useForm({
    mode: 'uncontrolled',
    initialValues: {
      leadId: null,
    },
  });

  const invoiceIdForm = useForm({
    mode: 'uncontrolled',
    initialValues: {
      invoiceRef: null,
    },
  });

  useEffect(() => {}, [companyList]);

  // Focus company input if a user has a specific user group
  useEffect(() => {
    const currentUser = AccountService.getUser();
    // You can modify this condition based on which user groups should trigger focus,
    // For example, focusing for PARTNER_EXPERT users
    if (currentUser && currentUser.departmentId != AdministratorModelConstants.ADMIN_DEPARTMENT_VALID) {
      // Small delay to ensure the drawer is fully rendered
      setTimeout(() => {
        companyInputRef.current?.focus();
      }, 100);
    } else {
      // If the user is not in the specific group, focus on leadIdInputRef
      setTimeout(() => {
        phoneInputRef.current?.focus();
      }, 100);
    }
  }, []);

  const autoComplete = async (value: string) => {
    const response = await MemberModelAPI.memberAutocomplete(value);
    setCompanyList(response);
  };
  const handleClose = () => {
    setCompanyList(new Page<MemberAutocompleteView>(MemberAutocompleteView));
    onClose();
  };

  const submitLeadIdForm = () => {
    let values = leadIdForm.getValues();

    if (!values.leadId) return;
    navigate(PartnerRfpUtils.getSearchResultLink({ id: values.leadId }));

    leadIdForm.setValues({
      leadId: null,
    });
    handleClose();
  };

  const submitInvoiceNumber = () => {
    let values = invoiceIdForm.getValues();

    if (!values.invoiceRef) return;
    navigate('/search-invoice', {
      state: {
        invoiceRef: values.invoiceRef,
      },
    });

    invoiceIdForm.setValues({
      invoiceRef: null,
    });
    handleClose();
  };

  const memberForm = useForm<MemberSearchRequestDTO>({
    mode: 'uncontrolled',
    initialValues: new MemberSearchRequestDTO(),
    validate: {
      vatNumber: (value) =>
        value && !/^[A-Za-z0-9]+$/.test(value)
          ? t('TVA must contain only letters and digits (no spaces or symbols)')
          : null,
    },
  });

  const submitMemberForm = () => {
    let values = memberForm.getValues();

    // Trim phone number before submission
    if (values.phone) {
      values.phone = values.phone.replace(/\s/g, '');
    }

    setCompanyList(new Page<MemberAutocompleteView>(MemberAutocompleteView));

    navigate('/search-user', {
      state: values,
    });

    memberForm.setValues(new MemberSearchRequestDTO());
    handleClose();
  };

  // Helper function to get company by ID
  const getCompanyById = (id: number) => {
    return companyList.content?.find((company) => company.id === id);
  };

  const getDataGrouped = () => {
    if (!companyList.content) return [];

    // Group companies by userGroupId
    const grouped = companyList.content.reduce(
      (acc, company) => {
        const groupKey = company.userGroupId.toString();
        if (!acc[groupKey]) {
          acc[groupKey] = [];
        }
        acc[groupKey].push({
          value: company.id.toString(),
          label: company.companyName,
        });
        return acc;
      },
      {} as Record<string, Array<{ value: string; label: string }>>
    );

    // Define preferred order for user groups
    const groupOrder = [
      UserGroupEnum.PARTNER_EXPERT,
      UserGroupEnum.PARTNER_EXPERT_TEASING,
      UserGroupEnum.EXPERT_CLASSIC,
      UserGroupEnum.PARTNER,
      UserGroupEnum.AFFILIATE,
      UserGroupEnum.PROSPECT,
      UserGroupEnum.BUYER,
      UserGroupEnum.TEAM_MEMBER,
      UserGroupEnum.ADMIN_SUPERUSER,
      UserGroupEnum.ADMIN_OPERATOR,
      UserGroupEnum.ADMIN_GOVEX,
    ];

    // Convert to Mantine's grouped format with user-friendly group names and proper ordering
    return Object.entries(grouped)
      .sort(([a], [b]) => {
        const aIndex = groupOrder.indexOf(Number(a) as UserGroupEnum);
        const bIndex = groupOrder.indexOf(Number(b) as UserGroupEnum);
        // If not found in order, put at the end
        const aOrder = aIndex === -1 ? 999 : aIndex;
        const bOrder = bIndex === -1 ? 999 : bIndex;
        return aOrder - bOrder;
      })
      .map(([userGroupId, items]) => ({
        group: UserGroupColorUtils.getUserGroupLabel(Number(userGroupId) as UserGroupEnum),
        items: items,
      }));
  };

  return (
    <>
      <Flex direction="column" gap="md" p="md">
        <form onSubmit={leadIdForm.onSubmit(submitLeadIdForm)}>
          <Flex direction="column" gap="md">
            <NumberInput
              leftSectionPointerEvents="none"
              leftSection={<IconEdit />}
              placeholder={t('Lead ID')}
              key={leadIdForm.key('leadId')}
              {...leadIdForm.getInputProps('leadId')}
            />
            <ActionIcon
              color="yellow"
              size="lg"
              component={'button'}
              className="link"
              variant="filled"
              type={'submit'}
              w="100%"
            >
              <Center>
                <IconSearch size={20} />
              </Center>
            </ActionIcon>
          </Flex>
        </form>
        <Divider size={'lg'} />

        <form onSubmit={invoiceIdForm.onSubmit(submitInvoiceNumber)}>
          <Flex direction="column" gap="md">
            <TextInput
              leftSectionPointerEvents="none"
              leftSection={<IconInvoice />}
              placeholder={t('Invoice number')}
              key={invoiceIdForm.key('invoiceRef')}
              {...invoiceIdForm.getInputProps('invoiceRef')}
            />
            <ActionIcon
              color="blue"
              size="lg"
              component={'button'}
              className="link"
              variant="filled"
              type={'submit'}
              w="100%"
            >
              <Center>
                <IconSearch size={20} />
              </Center>
            </ActionIcon>
          </Flex>
        </form>

        <Divider size={'lg'} />

        <form onSubmit={memberForm.onSubmit(submitMemberForm)}>
          <Flex direction="column" gap="md">
            <Autocomplete
              ref={companyInputRef}
              data={getDataGrouped()}
              onChange={(value) => {
                //if min length is 3, then search
                if (value.length < 1) {
                  setCompanyList(new Page<MemberAutocompleteView>(MemberAutocompleteView));
                }
                if (value.length >= 2) {
                  autoComplete(value).then();
                }
                memberForm.getInputProps('companyName').onChange(value);
              }}
              onOptionSubmit={(option) => {
                setTimeout(() => {
                  setCompanyList(new Page<MemberAutocompleteView>(MemberAutocompleteView));
                }, 1000);
                memberForm.reset();
                navigate(`/user/${option}/general`);
                onClose();
              }}
              renderOption={(input) => (
                <NavLink
                  p={0}
                  m={0}
                  component={Link}
                  to={'/user/' + input.option.value + '/general'}
                  style={{ cursor: 'pointer' }}
                  rightSection={(() => {
                    const company = getCompanyById(Number(input.option.value));
                    return <MemberStatusIcon status={company?.status} />;
                  })()}
                  leftSection={(() => {
                    const company = getCompanyById(Number(input.option.value));
                    return (
                      <Tooltip label={UserGroupColorUtils.getUserGroupLabel(company?.userGroupId)}>
                        <IconUser
                          color={UserGroupColorUtils.getUserGroupColor(company?.userGroupId)}
                        />
                      </Tooltip>
                    );
                  })()}
                  label={(input.option as any).label}
                />
              )}
              leftSectionPointerEvents="none"
              leftSection={<IconUser />}
              placeholder={t('Company')}
              key={memberForm.key('companyName')}
              value={memberForm.getInputProps('companyName').value}
              maxDropdownHeight={'400'}
            />
            <NumberInput
              leftSectionPointerEvents="none"
              placeholder={t('ID')}
              key={memberForm.key('userId')}
              {...memberForm.getInputProps('userId')}
            />
            <TextInput
              leftSectionPointerEvents="none"
              placeholder={t('Phone')}
              key={memberForm.key('tel')}
              {...memberForm.getInputProps('phone')}
              ref={phoneInputRef}
            />
            <TextInput
              leftSectionPointerEvents="none"
              placeholder={t('Email')}
              key={memberForm.key('email')}
              {...memberForm.getInputProps('email')}
            />

            <TextInput
              leftSectionPointerEvents="none"
              placeholder={t('Company VAT number')}
              key={memberForm.key('vatNumber')}
              {...memberForm.getInputProps('vatNumber')}
            />
            <ActionIcon
              color="red"
              size="lg"
              component={'button'}
              className="link"
              variant="filled"
              type={'submit'}
              w="100%"
            >
              <Center>
                <IconSearch size={20} />
              </Center>
            </ActionIcon>
          </Flex>
        </form>
      </Flex>
    </>
  );
}
